import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { UserPlus, Edit, Trash2, Send, Power, Shield, ShieldCheck, ShieldAlert, KeyRound, Copy } from 'lucide-react';
import { formatLastLogin, getLastLoginColor } from '../utils/lastLogin';

const SUBROLE_INFO = {
  total: {
    label: 'Administrador Total',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: ShieldCheck,
    desc: 'Acceso completo a todas las secciones (configuración, usuarios, finanzas, académico, RRHH).',
  },
  secretary: {
    label: 'Secretaría',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Shield,
    desc: 'Gestión de estudiantes, padres, anuncios, formularios y comunicaciones. Sin acceso a finanzas, nómina ni configuración.',
  },
  accounting: {
    label: 'Contabilidad',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: ShieldAlert,
    desc: 'Facturación, flujo de caja, nómina y RRHH. Sin acceso a estudiantes ni configuración.',
  },
};

export default function AdminUsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', admin_subrole: 'total' });
  const [credentialsModal, setCredentialsModal] = useState(null);

  const isTotal = user?.admin_subrole === 'total' || !user?.admin_subrole;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin-users');
      setUsers(res.data);
    } catch {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ full_name: '', email: '', admin_subrole: 'total' });
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ full_name: u.full_name, email: u.email, admin_subrole: u.admin_subrole || 'total' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) { toast.error('Completa nombre y email'); return; }
    try {
      if (editing) {
        await axios.put(`/admin-users/${editing.id}`, form);
        toast.success('Usuario actualizado');
      } else {
        const res = await axios.post('/admin-users', form);
        setCredentialsModal({
          username: res.data.user.username,
          password: res.data.temporary_password,
          full_name: res.data.user.full_name,
        });
      }
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.full_name}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`/admin-users/${u.id}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.put(`/admin-users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Usuario desactivado' : 'Usuario activado');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleSendCreds = async (u) => {
    if (!window.confirm(`Enviar nuevas credenciales a ${u.email}? Se generará una nueva contraseña.`)) return;
    try {
      const res = await axios.post(`/admin-users/${u.id}/send-credentials`);
      toast.success(res.data.message);
      if (res.data.temporary_password) {
        setCredentialsModal({
          username: u.username,
          password: res.data.temporary_password,
          full_name: u.full_name,
        });
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar credenciales');
    }
  };

  const copyCredentials = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  if (!isTotal) {
    return (
      <div className="p-6 text-center text-slate-500" data-testid="admin-users-no-access">
        <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>Solo el Administrador Total puede gestionar usuarios.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Gestión de Usuarios Administrativos</h3>
          <p className="text-sm text-slate-500">Crea cuentas para tu equipo con permisos según su rol.</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-admin-user-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Nuevo Usuario
        </Button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {Object.entries(SUBROLE_INFO).map(([key, info]) => {
          const Icon = info.icon;
          return (
            <div key={key} className={`border rounded-lg p-3 ${info.color}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" />
                <span className="font-semibold text-sm">{info.label}</span>
              </div>
              <p className="text-xs">{info.desc}</p>
            </div>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-slate-400 py-8">Cargando...</div>
      ) : (
        <div className="space-y-2">
          {users.map(u => {
            const sub = u.admin_subrole || 'total';
            const info = SUBROLE_INFO[sub];
            const Icon = info.icon;
            const isSelf = u.id === user?.user_id;
            return (
              <Card key={u.id} className={`${!u.is_active ? 'opacity-60' : ''}`} data-testid={`admin-user-${u.username}`}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${info.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-slate-800 truncate">{u.full_name}</span>
                        {isSelf && <Badge className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0">Tú</Badge>}
                        <Badge className={`${info.color} text-[10px]`}>{info.label}</Badge>
                        {!u.is_active && <Badge className="bg-red-50 text-red-700 text-[10px]">Inactivo</Badge>}
                        {u.credentials_sent && <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">✓ Credenciales enviadas</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 truncate">@{u.username} · {u.email}</p>
                      <p
                        className={`text-[10px] mt-0.5 ${getLastLoginColor(u.last_login_at)}`}
                        title={u.last_login_at ? new Date(u.last_login_at).toLocaleString('es-PR') : 'Nunca ha iniciado sesión'}
                        data-testid={`admin-last-login-${u.username}`}
                      >
                        Último acceso: {formatLastLogin(u.last_login_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} title="Editar"
                      data-testid={`edit-${u.username}`}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleSendCreds(u)} title="Enviar credenciales"
                      className="text-purple-600" data-testid={`send-creds-${u.username}`}>
                      <Send className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleToggleActive(u)} disabled={isSelf}
                      className={u.is_active ? 'text-red-500' : 'text-emerald-600'} title={u.is_active ? 'Desactivar' : 'Activar'}
                      data-testid={`toggle-${u.username}`}>
                      <Power className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(u)} disabled={isSelf}
                      className="text-red-500" title="Eliminar" data-testid={`delete-${u.username}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Usuario' : 'Nuevo Usuario Administrativo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label>Nombre completo *</Label>
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                placeholder="María Gómez" required data-testid="admin-user-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="maria@escuela.com" required data-testid="admin-user-email" />
            </div>
            <div>
              <Label>Rol *</Label>
              <Select value={form.admin_subrole} onValueChange={v => setForm({ ...form, admin_subrole: v })}>
                <SelectTrigger data-testid="admin-user-subrole"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SUBROLE_INFO).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">{SUBROLE_INFO[form.admin_subrole]?.desc}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-admin-user-btn">
                {editing ? 'Guardar' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials reveal modal */}
      <Dialog open={!!credentialsModal} onOpenChange={() => setCredentialsModal(null)}>
        <DialogContent className="max-w-md" data-testid="credentials-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-purple-600" />
              Credenciales generadas
            </DialogTitle>
          </DialogHeader>
          {credentialsModal && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Comparte estas credenciales con <strong>{credentialsModal.full_name}</strong>.
                Esta es la única vez que se mostrarán completas.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                <div>
                  <p className="text-xs text-slate-500">Usuario</p>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono text-slate-800">{credentialsModal.username}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyCredentials(credentialsModal.username)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Contraseña temporal</p>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono text-slate-800">{credentialsModal.password}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyCredentials(credentialsModal.password)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
              <Button onClick={() => copyCredentials(`Usuario: ${credentialsModal.username}\nContraseña: ${credentialsModal.password}`)}
                className="w-full" variant="outline">
                <Copy className="w-4 h-4 mr-1" /> Copiar ambos
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentialsModal(null)} className="w-full">Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
